/**
 * eKYB Service
 * Handles API calls to eKYB workflow endpoints (mock and production)
 */

// Azure Functions backend with REAL APIs (CORRECT URL)
const AZURE_BACKEND_URL = 'https://compliance-api-func-dev.azurewebsites.net/api';

// Legacy n8n endpoints (deprecated - using REAL APIs now)
const MOCK_BASE_URL = 'https://n8n-dev.transtarnetworks.eu/webhook/ekyb/mock';
const PROD_BASE_URL = 'https://n8n-dev.transtarnetworks.eu/webhook/ekyb';

class EKYBService {
  constructor() {
    // Use REAL Azure Functions backend by default
    this.baseURL = AZURE_BACKEND_URL;
    this.useRealAPIs = true;  // Enable real API mode
  }

  /**
   * Search for company by ID in specified jurisdiction
   * @param {string} jurisdiction - 'finland', 'sweden', or 'denmark'
   * @param {string} companyId - Company registration number
   * @returns {Promise<Object>} Company verification data
   */
  async searchCompany(jurisdiction, companyId) {
    try {
      // Map jurisdiction to country code
      const countryCodeMap = {
        'finland': 'FI',
        'sweden': 'SE',
        'denmark': 'DK'
      };
      const countryCode = countryCodeMap[jurisdiction];

      if (!countryCode) {
        throw new Error(`Unsupported jurisdiction: ${jurisdiction}`);
      }

      // Call REAL Azure Functions companies/verify endpoint
      const response = await fetch(`${this.baseURL}/companies/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          countryCode,
          companyId
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `Company search failed: ${response.status}`);
      }

      const result = await response.json();

      // Check if verification was successful
      if (!result.verified) {
        throw new Error(result.error || 'Company verification failed');
      }

      // Return company data in eKYB-compatible format
      return {
        verified: result.verified,
        businessId: result.company.business_id,
        name: result.company.name,
        registrationDate: result.company.registration_date,
        companyForm: result.company.verification_data?.companyForms?.[0]?.descriptions?.find(d => d.languageCode === '3')?.description || 'N/A',
        status: result.company.status,
        address: result.company.address,
        website: result.company.website,
        industry: result.company.industry,
        raw: result.company.verification_data
      };
    } catch (error) {
      console.error('Company search error:', error);
      throw error;
    }
  }

  /**
   * Perform AML screening on entity and UBOs
   * @param {Object} data - Screening data
   * @param {string} data.entityName - Company name
   * @param {Array} data.ubos - Array of UBO objects with name, dob, nationality, ownership
   * @returns {Promise<Object>} AML screening results
   */
  async performAMLScreening(data) {
    try {
      // Call REAL Azure Functions AML screening endpoint
      const response = await fetch(`${this.baseURL}/aml-screening`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `AML screening failed: ${response.status}`);
      }

      const result = await response.json();

      // Check if screening was successful
      if (!result.success) {
        throw new Error(result.error || 'AML screening failed');
      }

      return result;
    } catch (error) {
      console.error('AML screening error:', error);
      throw error;
    }
  }

  /**
   * Search for companies by name
   * @param {string} jurisdiction - 'finland', 'sweden', or 'denmark'
   * @param {string} companyName - Company name to search for
   * @returns {Promise<Object>} Search results
   */
  async searchCompaniesByName(jurisdiction, companyName) {
    try {
      const countryCodeMap = {
        'finland': 'FI',
        'sweden': 'SE',
        'denmark': 'DK'
      };
      const countryCode = countryCodeMap[jurisdiction];

      if (!countryCode) {
        throw new Error(`Unsupported jurisdiction: ${jurisdiction}`);
      }

      const response = await fetch(`${this.baseURL}/companies/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          countryCode,
          companyName,
          maxResults: 10
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Company search failed: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Company search failed');
      }

      return result;
    } catch (error) {
      console.error('Company name search error:', error);
      throw error;
    }
  }

  /**
   * Check MiCA register for CASP authorization
   * @param {string} entityName - Company name to check
   * @param {string} jurisdiction - Country code
   * @returns {Promise<Object>} MiCA registration status
   */
  async checkMiCARegister(entityName, jurisdiction) {
    try {
      // Mock MiCA check - in production this would call actual MiCA register API
      const response = await fetch(`${this.baseURL}/mica-check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ entityName, jurisdiction }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `MiCA check failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('MiCA check error:', error);
      // Return default result if endpoint doesn't exist yet
      return {
        registered: false,
        status: 'NOT_REGISTERED',
        message: 'Entity not found in MiCA CASP register'
      };
    }
  }

  /**
   * Generate verification report
   * @param {Object} verificationData - Complete verification data
   * @returns {Promise<Object>} Report generation result
   */
  async generateReport(verificationData) {
    try {
      const response = await fetch(`${this.baseURL}/generate-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(verificationData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Report generation failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Report generation error:', error);
      // Return mock report ID if endpoint doesn't exist yet
      return {
        reportId: `EKYB-${Date.now()}`,
        timestamp: new Date().toISOString(),
        status: 'GENERATED'
      };
    }
  }

  /**
   * Switch to production endpoints
   */
  useProduction() {
    this.baseURL = PROD_BASE_URL;
  }

  /**
   * Switch to mock endpoints (default)
   */
  useMock() {
    this.baseURL = MOCK_BASE_URL;
  }

  /**
   * Format company ID based on jurisdiction
   * @param {string} jurisdiction
   * @param {string} companyId
   * @returns {string} Formatted company ID
   */
  formatCompanyId(jurisdiction, companyId) {
    const cleaned = companyId.replace(/[^0-9A-Z-]/gi, '').toUpperCase();

    switch (jurisdiction) {
      case 'finland':
        // Format: 1234567-8
        return cleaned.replace(/(\d{7})(\d)/, '$1-$2');
      case 'sweden':
        // Format: 556789-0123
        return cleaned.replace(/(\d{6})(\d{4})/, '$1-$2');
      case 'denmark':
        // Format: 12345678
        return cleaned;
      default:
        return cleaned;
    }
  }

  /**
   * Validate company ID format
   * @param {string} jurisdiction
   * @param {string} companyId
   * @returns {Object} { valid: boolean, message: string }
   */
  validateCompanyId(jurisdiction, companyId) {
    const cleaned = companyId.replace(/[^0-9-]/g, '');

    switch (jurisdiction) {
      case 'finland':
        // YTJ format: 7 digits, hyphen, 1 check digit (1234567-8)
        if (!/^\d{7}-\d$/.test(cleaned)) {
          return {
            valid: false,
            message: 'Finnish Business ID must be in format: 1234567-8'
          };
        }
        return { valid: true };

      case 'sweden':
        // Swedish format: 6 digits, hyphen, 4 digits (556789-0123)
        if (!/^\d{6}-\d{4}$/.test(cleaned)) {
          return {
            valid: false,
            message: 'Swedish Organization Number must be in format: 556789-0123'
          };
        }
        return { valid: true };

      case 'denmark':
        // CVR format: 8 digits (12345678)
        if (!/^\d{8}$/.test(cleaned)) {
          return {
            valid: false,
            message: 'Danish CVR number must be 8 digits'
          };
        }
        return { valid: true };

      default:
        return { valid: false, message: 'Unknown jurisdiction' };
    }
  }

  /**
   * Get example company ID for jurisdiction (REAL companies)
   * @param {string} jurisdiction
   * @returns {string} Example company ID
   */
  getExampleId(jurisdiction) {
    switch (jurisdiction) {
      case 'finland':
        return '0112038-9';  // Nokia Oyj (REAL)
      case 'sweden':
        return '556016-0680';  // Ericsson (REAL)
      case 'denmark':
        return '10150817';  // Erhvervsstyrelsen (REAL)
      default:
        return '';
    }
  }
}

export default new EKYBService();
